import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  getUserDayoffs,
  deleteDayoff,
  getUsersSummary,
} from "../../../api/api";
import "./DayoffDeletion.css";

const DayoffDeletion = () => {
  const [dayoffs, setDayoffs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);
  const [isLoadingDayoffs, setIsLoadingDayoffs] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState(null);

  // Загрузка всех сотрудников
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setIsLoadingEmployees(true);
        const response = await getUsersSummary({
          isActive: true,
          pageSize: 10000,
        });

        const formattedEmployees = response.users.map((user) => ({
          value: user.id,
          label: `${user.surname} ${user.name} ${user.patronymic || ""}`.trim(),
        }));

        // Сортируем сотрудников по имени
        formattedEmployees.sort((a, b) => a.label.localeCompare(b.label, "ru"));

        setEmployees(formattedEmployees);
      } catch (error) {
        console.error("Ошибка при загрузке сотрудников:", error);
        setError("Ошибка при загрузке сотрудников");
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, []);

  // Загрузка отгулов выбранного сотрудника
  useEffect(() => {
    const loadDayoffs = async () => {
      if (!selectedEmployee) {
        setDayoffs([]);
        return;
      }

      try {
        setIsLoadingDayoffs(true);
        setError(null);
        const response = await getUserDayoffs(selectedEmployee.value);
        const dayoffsData = response.days_off || [];

        const formattedDayoffs = dayoffsData
          .filter((dayoff) => dayoff && dayoff.id)
          .map((dayoff) => {
            const hours =
              typeof dayoff.time === "number"
                ? dayoff.time / 3600
                : parseFloat(dayoff.time);

            const dateObj = dayoff.date_of_use
              ? new Date(dayoff.date_of_use)
              : null;

            return {
              id: dayoff.id,
              userId: selectedEmployee.value,
              userName: selectedEmployee.label,
              date: dateObj ? dateObj.toLocaleDateString("ru-RU") : "-",
              dateObj: dateObj, // Сохраняем объект даты для сортировки
              hours: hours,
            };
          })
          .sort((a, b) => {
            // Сортируем по дате: сначала свежие (новые), потом старые
            if (!a.dateObj && !b.dateObj) return 0;
            if (!a.dateObj) return 1;
            if (!b.dateObj) return -1;
            return b.dateObj - a.dateObj; // Убывание: новые первыми
          });

        setDayoffs(formattedDayoffs);
      } catch (error) {
        console.error("Ошибка при загрузке отгулов:", error);
        setError("Ошибка при загрузке отгулов");
        setDayoffs([]);
      } finally {
        setIsLoadingDayoffs(false);
      }
    };

    loadDayoffs();
  }, [selectedEmployee]);

  const handleDelete = (dayoffId) => {
    setPendingDeleteId(dayoffId);
    setShowConfirmPopup(true);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;

    try {
      setIsDeleting(true);
      setShowConfirmPopup(false);

      await deleteDayoff(pendingDeleteId);

      // Удаляем отгул из списка
      setDayoffs((prev) =>
        prev.filter((dayoff) => dayoff.id !== pendingDeleteId)
      );

      setError(null);
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    } catch (error) {
      setError(`Ошибка при удалении отгула: ${error.message}`);
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setError(null);
      }, 3000);
    } finally {
      setIsDeleting(false);
      setPendingDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmPopup(false);
    setPendingDeleteId(null);
  };

  const clearFilters = () => {
    setSelectedEmployee(null);
    setDayoffs([]);
  };

  return (
    <div className="dayoff-deletion">
      <h2 className="dayoff-deletion__title">Удаление отгулов</h2>

      <div className="dayoff-deletion__filters">
        <div className="form-group">
          <label>Сотрудник</label>
          <Select
            value={selectedEmployee}
            onChange={setSelectedEmployee}
            options={employees}
            className="employee-select"
            placeholder={
              isLoadingEmployees
                ? "Загрузка сотрудников..."
                : "Выберите сотрудника"
            }
            isClearable
            isSearchable
            isLoading={isLoadingEmployees}
            isDisabled={isLoadingEmployees}
            noOptionsMessage={({ inputValue }) =>
              inputValue ? "Сотрудник не найден" : "Нет сотрудников"
            }
          />
        </div>

        {selectedEmployee && (
          <button
            className="clear-filters-button"
            onClick={clearFilters}
            disabled={isDeleting}
          >
            Сбросить фильтр
          </button>
        )}
      </div>

      {error && !showPopup && (
        <div className="dayoff-deletion__error">{error}</div>
      )}

      <div className="dayoff-deletion__table-container">
        {isLoadingDayoffs ? (
          <div className="loading">Загрузка отгулов...</div>
        ) : dayoffs.length > 0 ? (
          <table className="dayoff-deletion__table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Часы</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {dayoffs.map((dayoff, index) => (
                <tr key={`${dayoff.id}-${index}`}>
                  <td>{dayoff.date}</td>
                  <td>{dayoff.hours}</td>
                  <td>
                    <button
                      className="delete-button"
                      onClick={() => handleDelete(dayoff.id)}
                      disabled={isDeleting}
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dayoff-deletion__empty">
            {selectedEmployee
              ? "У выбранного сотрудника нет отгулов"
              : "Выберите сотрудника для просмотра отгулов"}
          </div>
        )}
      </div>

      {showConfirmPopup && (
        <div className="popup-overlay">
          <div className="popup popup-confirm">
            <p className="popup-text">
              Вы уверены, что хотите удалить этот отгул?
            </p>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {showPopup && (
        <div className="popup-overlay">
          <div className={`popup ${error ? "popup-error" : ""}`}>
            <div
              className={`popup-icon ${error ? "popup-icon-error" : ""}`}
            ></div>
            <p className="popup-text">{error || "Отгул успешно удален"}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayoffDeletion;
